// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Constants ----------------------------------------------------------------------------------

/** How long the actions wait before getting out of the way again. */
const AUTO_HIDE_MS = 4000;

// --- Types --------------------------------------------------------------------------------------

type Props = {
    phone: string;
    isOpen: boolean;
    onToggle: () => void;
    /** Anything that belongs to the customer rather than to the order — the regular badge. */
    children?: ReactNode;
    size?: "sm" | "lg";
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * The customer's number, and the three things anyone ever does with it.
 *
 * The number never leaves: the actions float **over** it on a translucent sheet, so what is behind
 * them stays readable as a shape without competing with them. That is also what makes this a visit
 * rather than a mode — nothing moved, something was laid on top, and it lifts off again.
 *
 * It lifts off by itself after four seconds. Anyone who tapped the number by accident, or changed
 * their mind, gets the number back without having to find the way out — and the close button is
 * there for anyone who does not want to wait.
 */
export default function OrderContacts({ phone, isOpen, onToggle, children, size = "sm" }: Props) {
    const t = useTranslation();
    const [copied, setCopied] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** `wa.me` and `tel:` both want the digits alone — a saved "+20 10…" reaches neither. */
    const digits = phone.replace(/\D/g, "");

    useEffect(() => {
        if (!isOpen) return;
        timerRef.current = setTimeout(onToggle, AUTO_HIDE_MS);
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [isOpen]);

    // --- Handlers --------------------------------------------------------------------------------

    const handleCopy = async () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        try {
            await navigator.clipboard.writeText(phone);
            setCopied(true);
        } catch {
            // A browser that refuses the clipboard leaves the number on screen to be read instead.
        }
        // Long enough for the tick to register as an answer, short enough not to be a state.
        setTimeout(() => {
            setCopied(false);
            onToggle();
        }, 900);
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="order-contacts" data-size={size} data-open={isOpen}>
            <button type="button" className="order-contacts-phone" onClick={onToggle} dir="ltr">
                <span className="order-contacts-number">{phone}</span>
                {children}
            </button>

            {isOpen && (
                <div className="order-contacts-actions">
                    <button
                        type="button"
                        className="order-contacts-btn"
                        onClick={handleCopy}
                        aria-label={t.orders.copyPhone}
                        title={t.orders.copyPhone}
                    >
                        <img src={copied ? "/icons/check-icon.svg" : "/icons/copy-icon.svg"} alt="" width={14} height={14} />
                    </button>

                    <a
                        className="order-contacts-btn"
                        href={`tel:${phone}`}
                        onClick={onToggle}
                        aria-label={t.orders.callPhone}
                        title={t.orders.callPhone}
                    >
                        <img src="/icons/phone-icon.svg" alt="" width={14} height={14} />
                    </a>

                    <a
                        className="order-contacts-btn"
                        href={`https://wa.me/${digits}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={onToggle}
                        aria-label={t.orders.whatsappPhone}
                        title={t.orders.whatsappPhone}
                    >
                        <img src="/icons/whatsapp-icon.svg" alt="" width={14} height={14} />
                    </a>

                    <button
                        type="button"
                        className="order-contacts-btn order-contacts-btn--close"
                        onClick={onToggle}
                        aria-label={t.orders.editCancelLabel}
                        title={t.orders.editCancelLabel}
                    >
                        <img src="/icons/close-icon.svg" alt="" width={12} height={12} />
                    </button>
                </div>
            )}
        </div>
    );
}
